/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the app.
 * Supports optional authentication - users can play as guests.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { getSupabaseClient, isAuthAvailable } from '../services/supabaseClient';

export type AccountStatus = 'user' | 'moderator' | 'admin';

export interface AccountInfo {
  id: string;
  email: string;
  displayName: string | null;
  username: string | null;
  status: AccountStatus;
}

export interface ReferralInfo {
  referralCode: string;
  referredBy: string | null;
  referralCount: number;
  pendingRewards: number;
}

export interface DailyRewardInfo {
  currentStreak: number;
  longestStreak: number;
  lastClaimDate: string | null;
  canClaimToday: boolean;
  todayReward: { day: number; pieces: number; bonusItem?: string };
  nextRewards: Array<{ day: number; pieces: number; bonusItem?: string }>;
  totalClaims: number;
  totalPiecesEarned: number;
}

export interface Character {
  id: string;
  accountId: string;
  name: string;
  characterType: string;
  shipColor: string;
  x: number;
  y: number;
  berries: number;
  inventory: unknown[];
  shipType: string;
  shipName: string | null;
  shipHealth: number;
  shipMaxHealth: number;
  level: number;
  experience: number;
  reputation: number;
  infamy: number;
  crewId: string | null;
  createdAt: string;
  lastPlayedAt: string;
  totalPlaytimeMinutes: number;
  skills?: unknown;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  accountInfo: AccountInfo | null;
  characters: Character[];
  isLoading: boolean;
  isCharactersLoading: boolean;
  isAccountInfoLoading: boolean;
  isAuthEnabled: boolean;
  error: string | null;
  signUp: (email: string, password: string) => Promise<boolean>;
  signIn: (email: string, password: string) => Promise<boolean>;
  signInWithGoogle: () => Promise<boolean>;
  signInWithDiscord: () => Promise<boolean>;
  signOut: () => Promise<void>;
  loadCharacters: () => Promise<void>;
  loadAccountInfo: () => Promise<void>;
  checkUsernameAvailable: (username: string) => Promise<boolean>;
  checkCharacterNameAvailable: (name: string) => Promise<{ available: boolean; error?: string }>;
  claimUsername: (username: string) => Promise<{ success: boolean; error?: string }>;
  createCharacter: (name: string, characterType: string, shipColor: string) => Promise<Character | null>;
  saveCharacter: (characterId: string, updates: Partial<Character>) => Promise<boolean>;
  deleteCharacter: (characterId: string) => Promise<boolean>;
  clearError: () => void;
  getReferralInfo: () => Promise<ReferralInfo | null>;
  applyReferralCode: (code: string, characterId?: string) => Promise<{ success: boolean; error?: string }>;
  claimReferralRewards: (characterId?: string) => Promise<{ success: boolean; piecesClaimed: number; error?: string }>;
  getDailyRewardInfo: () => Promise<DailyRewardInfo | null>;
  claimDailyReward: () => Promise<{ success: boolean; piecesEarned: number; bonusItem?: string; newStreak: number; error?: string }>;
  resendConfirmationEmail: (email: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCharactersLoading, setIsCharactersLoading] = useState<boolean>(false);
  const [isAccountInfoLoading, setIsAccountInfoLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthEnabled = isAuthAvailable();
  const supabase = getSupabaseClient();
  const serverUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Initialize auth state
  useEffect(() => {
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    // Handle OAuth callback from URL hash
    const handleOAuthCallback = async (): Promise<void> => {
      const hash = window.location.hash;
      if (hash.includes('access_token')) {
        try {
          const { data, error } = await supabase.auth.getSession();
          if (error) {
            console.error('Error getting session after OAuth:', error);
          } else if (data.session) {
            setSession(data.session);
            setUser(data.session.user ?? null);
            // Clean up the hash from URL
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (err) {
          console.error('Failed to handle OAuth callback:', err);
        }
      }
    };

    // Get initial session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setIsLoading(false);
      // Also handle OAuth callback if needed
      handleOAuthCallback();
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase]);

  // Load characters and account info when user changes
  useEffect(() => {
    if (user && session) {
      loadCharacters();
      loadAccountInfo();
    } else {
      setCharacters([]);
      setAccountInfo(null);
      setIsCharactersLoading(false);
      setIsAccountInfoLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, session]);

  const loadCharacters = useCallback(async (): Promise<void> => {
    if (!session?.access_token) return;

    try {
      setIsCharactersLoading(true);
      const response = await fetch(`${serverUrl}/api/auth/characters`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCharacters(data.characters || []);
      }
    } catch (err) {
      console.error('Failed to load characters:', err);
    } finally {
      setIsCharactersLoading(false);
    }
  }, [session, serverUrl]);

  const loadAccountInfo = useCallback(async (): Promise<void> => {
    if (!session?.access_token) return;

    try {
      setIsAccountInfoLoading(true);
      const response = await fetch(`${serverUrl}/api/auth/account`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAccountInfo(data);
      }
    } catch (err) {
      console.error('Failed to load account info:', err);
    } finally {
      setIsAccountInfoLoading(false);
    }
  }, [session, serverUrl]);

  const checkUsernameAvailable = useCallback(async (username: string): Promise<boolean> => {
    try {
      const response = await fetch(`${serverUrl}/api/auth/username/check/${encodeURIComponent(username)}`);
      if (response.ok) {
        const data = await response.json();
        return data.available === true;
      }
      if (response.status === 400) {
        const data = await response.json();
        throw new Error(data.error || 'Invalid username format');
      }
      throw new Error('Failed to check username availability');
    } catch (err) {
      console.error('Failed to check username availability:', err);
      throw err;
    }
  }, [serverUrl]);

  const checkCharacterNameAvailable = useCallback(async (name: string): Promise<{ available: boolean; error?: string }> => {
    try {
      const response = await fetch(`${serverUrl}/api/auth/characters/check/${encodeURIComponent(name)}`);
      const data = await response.json();
      if (response.ok) {
        return { available: true };
      }
      if (response.status === 400 || response.status === 409) {
        return { available: false, error: data?.error || 'Name unavailable' };
      }
      return { available: false, error: 'Failed to check availability' };
    } catch (err) {
      console.error('Failed to check character name availability:', err);
      throw err;
    }
  }, [serverUrl]);

  const claimUsername = useCallback(async (username: string): Promise<{ success: boolean; error?: string }> => {
    if (!session?.access_token) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const response = await fetch(`${serverUrl}/api/auth/username/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ username }),
      });

      const data = await response.json();

      if (response.ok) {
        // Update local account info with new username
        setAccountInfo((prev) => prev ? { ...prev, username: data.username } : null);
        return { success: true };
      }

      return { success: false, error: data.error || 'Failed to claim username' };
    } catch (err) {
      console.error('Failed to claim username:', err);
      return { success: false, error: 'Failed to claim username' };
    }
  }, [session, serverUrl]);

  const signUp = async (email: string, password: string): Promise<boolean> => {
    if (!supabase) {
      setError('Authentication not available');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (signUpError) {
        setError(signUpError.message);
        return false;
      }

      if (data.user) {
        return true;
      }

      return false;
    } catch (err) {
      setError('Failed to sign up');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string): Promise<boolean> => {
    if (!supabase) {
      setError('Authentication not available');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        // Supabase returns "Invalid login credentials" for both wrong password AND unconfirmed email
        // Provide a helpful message to guide users
        if (signInError.message && signInError.message.toLowerCase().includes('invalid login credentials')) {
          setError('Invalid email or password. If you just signed up, please check your email for a confirmation link and verify your account before signing in.');
          return false;
        }
        
        setError(signInError.message);
        return false;
      }

      if (data.user) {
        return true;
      }

      return false;
    } catch (err) {
      setError('Failed to sign in');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async (): Promise<boolean> => {
    if (!supabase) {
      setError('Authentication not available');
      return false;
    }

    setIsLoading(true);
    setError(null);

    const isInIframe: boolean = (() => {
      try {
        return window.self !== window.top;
      } catch (_error) {
        return true;
      }
    })();

    const redirectTo: string = (() => {
      try {
        return window.top?.location?.href || window.location.href;
      } catch (_error) {
        return window.location.href;
      }
    })();

    try {
      const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          skipBrowserRedirect: true,
        },
      });

      if (oauthError) {
        setError(oauthError.message);
        return false;
      }

      const oauthUrl: string | undefined = data?.url;
      if (!oauthUrl) {
        setError('OAuth URL not provided');
        return false;
      }

      if (isInIframe) {
        try {
          window.top?.location.assign(oauthUrl);
          return true;
        } catch (_error) {
          window.open(oauthUrl, '_blank', 'noopener,noreferrer');
          return true;
        }
      }

      window.location.assign(oauthUrl);

      // OAuth redirects, so we won't reach here immediately
      return true;
    } catch (err) {
      setError('Failed to sign in with Google');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithDiscord = async (): Promise<boolean> => {
    if (!supabase) {
      setError('Authentication not available');
      return false;
    }

    setIsLoading(true);
    setError(null);

    const isInIframe: boolean = (() => {
      try {
        return window.self !== window.top;
      } catch (_error) {
        return true;
      }
    })();

    const redirectTo: string = (() => {
      try {
        return window.top?.location?.href || window.location.href;
      } catch (_error) {
        return window.location.href;
      }
    })();

    try {
      const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'discord',
        options: {
          redirectTo,
          skipBrowserRedirect: true,
        },
      });

      if (oauthError) {
        setError(oauthError.message);
        return false;
      }

      const oauthUrl: string | undefined = data?.url;
      if (!oauthUrl) {
        setError('OAuth URL not provided');
        return false;
      }

      if (isInIframe) {
        try {
          window.top?.location.assign(oauthUrl);
          return true;
        } catch (_error) {
          window.open(oauthUrl, '_blank', 'noopener,noreferrer');
          return true;
        }
      }

      window.location.assign(oauthUrl);

      // OAuth redirects, so we won't reach here immediately
      return true;
    } catch (err) {
      setError('Failed to sign in with Discord');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    setIsLoading(true);
    try {
      if (supabase) {
        await supabase.auth.signOut();
      }
      // Clear local state regardless of Supabase availability
      setUser(null);
      setSession(null);
      setCharacters([]);
      setAccountInfo(null);
      setIsCharactersLoading(false);
    } catch (err) {
      console.error('Failed to sign out:', err);
      // Clear state even on error
      setUser(null);
      setSession(null);
      setCharacters([]);
      setAccountInfo(null);
      setIsCharactersLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const createCharacter = async (
    name: string,
    characterType: string,
    shipColor: string
  ): Promise<Character | null> => {
    if (!session?.access_token) {
      setError('Not authenticated');
      return null;
    }

    try {
      const response = await fetch(`${serverUrl}/api/auth/characters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ name, characterType, shipColor }),
      });

      if (response.ok) {
        const data = await response.json();
        const newCharacter = data.character;
        setCharacters((prev) => [newCharacter, ...prev]);
        return newCharacter;
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create character');
        return null;
      }
    } catch (err) {
      setError('Failed to create character');
      return null;
    }
  };

  const saveCharacter = async (characterId: string, updates: Partial<Character>): Promise<boolean> => {
    if (!session?.access_token) return false;

    try {
      const response = await fetch(`${serverUrl}/api/auth/characters/${characterId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(updates),
      });

      return response.ok;
    } catch (err) {
      console.error('Failed to save character:', err);
      return false;
    }
  };

  const deleteCharacter = async (characterId: string): Promise<boolean> => {
    if (!session?.access_token) return false;

    try {
      const response = await fetch(`${serverUrl}/api/auth/characters/${characterId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        setCharacters((prev) => prev.filter((c) => c.id !== characterId));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to delete character:', err);
      return false;
    }
  };

  const clearError = (): void => {
    setError(null);
  };

  const getReferralInfo = useCallback(async (): Promise<ReferralInfo | null> => {
    if (!session?.access_token) return null;

    try {
      const response = await fetch(`${serverUrl}/api/auth/referral`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (err) {
      console.error('Failed to get referral info:', err);
      return null;
    }
  }, [session, serverUrl]);

  const applyReferralCode = useCallback(async (code: string, characterId?: string): Promise<{ success: boolean; error?: string }> => {
    if (!session?.access_token) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const response = await fetch(`${serverUrl}/api/auth/referral/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ referralCode: code, characterId }),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true };
      }

      return { success: false, error: data.error || 'Failed to apply referral code' };
    } catch (err) {
      console.error('Failed to apply referral code:', err);
      return { success: false, error: 'Failed to apply referral code' };
    }
  }, [session, serverUrl]);

  const claimReferralRewards = useCallback(async (characterId?: string): Promise<{ success: boolean; piecesClaimed: number; error?: string }> => {
    if (!session?.access_token) {
      return { success: false, piecesClaimed: 0, error: 'Not authenticated' };
    }

    try {
      const response = await fetch(`${serverUrl}/api/auth/referral/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ characterId }),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, piecesClaimed: data.piecesClaimed || 0 };
      }

      return { success: false, piecesClaimed: 0, error: data.error || 'Failed to claim rewards' };
    } catch (err) {
      console.error('Failed to claim referral rewards:', err);
      return { success: false, piecesClaimed: 0, error: 'Failed to claim rewards' };
    }
  }, [session, serverUrl]);

  const getDailyRewardInfo = useCallback(async (): Promise<DailyRewardInfo | null> => {
    if (!session?.access_token) return null;

    try {
      const response = await fetch(`${serverUrl}/api/auth/daily-rewards`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (err) {
      console.error('Failed to get daily reward info:', err);
      return null;
    }
  }, [session, serverUrl]);

  const claimDailyReward = useCallback(async (characterId?: string): Promise<{ success: boolean; piecesEarned: number; bonusItem?: string; newStreak: number; error?: string }> => {
    if (!session?.access_token) {
      return { success: false, piecesEarned: 0, newStreak: 0, error: 'Not authenticated' };
    }

    try {
      const response = await fetch(`${serverUrl}/api/auth/daily-rewards/claim`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ characterId }),
      });

      const data = await response.json();

      if (response.ok) {
        return {
          success: true,
          piecesEarned: data.piecesEarned || 0,
          bonusItem: data.bonusItem,
          newStreak: data.newStreak || 0,
        };
      }

      return { success: false, piecesEarned: 0, newStreak: 0, error: data.error || 'Failed to claim reward' };
    } catch (err) {
      console.error('Failed to claim daily reward:', err);
      return { success: false, piecesEarned: 0, newStreak: 0, error: 'Failed to claim reward' };
    }
  }, [session, serverUrl]);

  const resendConfirmationEmail = useCallback(async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${serverUrl}/api/auth/resend-confirmation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true };
      }

      return { success: false, error: data.error || 'Failed to resend confirmation email' };
    } catch (err) {
      console.error('Failed to resend confirmation email:', err);
      return { success: false, error: 'Failed to resend confirmation email' };
    }
  }, [serverUrl]);

  const value: AuthContextType = {
    user,
    session,
    accountInfo,
    characters,
    isLoading,
    isCharactersLoading,
    isAccountInfoLoading,
    isAuthEnabled,
    error,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithDiscord,
    signOut,
    loadCharacters,
    loadAccountInfo,
    checkUsernameAvailable,
    checkCharacterNameAvailable,
    claimUsername,
    createCharacter,
    saveCharacter,
    deleteCharacter,
    clearError,
    getReferralInfo,
    applyReferralCode,
    claimReferralRewards,
    getDailyRewardInfo,
    claimDailyReward,
    resendConfirmationEmail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
